// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { View, Button } from "@components";
import type { StepProps } from "@components";
import { AlertCriteriaForm } from "@modules/service/components";
import type { Alert, AlertEmployeeExclusionReasons } from "@modules/service/reducer/alerts/types";

type StepData = $Exact<{
  details: Object,
  employeeExclusionReasons: Array<Object>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepAlertsCriteria extends React.Component<Props> {
  validateDetails: *;
  validateExclusionReasons: *;

  componentDidMount() {
    this.validateDetails = new Ajv({ multipleOfPrecision: 2 }).compile(this.schema.details);
    this.validateExclusionReasons = new Ajv({ multipleOfPrecision: 2 }).compile(
      this.schema.exclusionReasons,
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (this.schema && prevProps.schema !== this.schema) {
      this.validateDetails = new Ajv({ multipleOfPrecision: 2 }).compile(this.schema.details);
      this.validateExclusionReasons = new Ajv({ multipleOfPrecision: 2 }).compile(
        this.schema.exclusionReasons,
      );
    }
  }

  get data(): StepData {
    return {
      details: (this.props.draft || {}).content.details || {},
      employeeExclusionReasons: (this.props.draft || {}).content.employeeExclusionReasons || [],
    };
  }

  get schema() {
    return {
      details: (this.props.schema || {}).properties.details,
      exclusionReasons: (this.props.schema || {}).properties.employeeExclusionReasons,
    };
  }

  isValid = (data: StepData) => {
    if (!this.validateDetails) {
      return false;
    }

    const isValid = this.validateDetails(removeNullAndUndefinedProps(data.details));
    if (!isValid) {
      console.warn("isValid errors", this.validateDetails.errors, data.details);
    }
    return isValid;
  };

  submitForm = () => {
    this.props.handleSubmit(this.data, true);

    if (this.form) {
      this.form.current.submit();
    }
  };

  submitDetails = (formData: Object) => {
    if (this.props.handleSubmit) {
      this.updateDetails({ formData });
      this.props.handleSubmit({}, true);
    }
  };

  updateDetails = (formData: Object) => {
    let manipulatedFormData = formData;

    if (
      formData.alertType === "ABSENCE_REASON_ALERT_TYPE" &&
      !((formData.absenceReasonAlertType || {}).absenceReasons || {}).length
    ) {
      manipulatedFormData = {
        ...formData,
        absenceReasonAlertType: {
          absenceReasons: [
            /* absence reasons will be populated in step 3 */
          ],
        },
      };
    }

    if (
      formData.alertType === "X_SPELLS_OF_SAME_REASON_IN_Y_PERIOD_TYPE" &&
      !((formData.xSpellsOfSameReasonInYPeriodType || {}).absenceReasons || {}).length
    ) {
      manipulatedFormData = {
        ...formData,
        xSpellsOfSameReasonInYPeriodType: {
          ...formData.xSpellsOfSameReasonInYPeriodType,
          absenceReasons: [
            /* absence reasons will be populated in step 3 */
          ],
        },
      };
    }

    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        details: {
          ...(this.props.draft?.content.details || {}),
          ...manipulatedFormData,
        },
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <View gap={3}>
        {this.schema && this.data && (
          <AlertCriteriaForm
            data={this.data.details}
            schema={this.schema.details}
            onChange={this.updateDetails}
            onSubmit={this.submitDetails}
          />
        )}
        {!this.props.editing && (
          <View direction="row" justifyContent="end">
            <Button
              materialIcon="fast_forward"
              onClick={this.submitForm}
              disabled={!this.isValid(this.data)}
              label={this.props.t("NEXT")}
            />
          </View>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(StepAlertsCriteria);
